import { APP_VERSION_RELOAD_MARKER_KEY } from "./app-version-constants";
import { markPreloadErrorHandled } from "./app-version-error-deduplication";
import { compareDeploymentVersions, normalizeDeploymentVersion } from "./deployment-version";
import { isNetworkFetchFailure } from "./network-errors";
import { fetchLatestRuntimeConfig } from "./runtime-config";

export const APP_VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000;
export const APP_VERSION_MIN_CHECK_INTERVAL_MS = 60 * 1000;
export const APP_VERSION_RELOAD_MARKER_TTL_MS = 10 * 60 * 1000;
export const APP_VERSION_RELOAD_WATCHDOG_MS = 1500;
// With the 5-minute poll, 10–15 minutes of an unreachable /df-config.json while online.
export const APP_VERSION_UNREACHABLE_CHECKS_BEFORE_FAILURE = 3;

const RELOAD_MARKER_SCHEMA_VERSION = 1;

export type AppVersionStatus =
  | "current"
  | "checking"
  | "update_available"
  | "reload_deferred_offline"
  | "reload_attempted"
  | "manual_recovery_required";

export type AppVersionRecoveryTrigger =
  | "bootstrap_mismatch"
  | "navigation_mismatch"
  | "preload_error";

export type AppVersionSnapshot = {
  status: AppVersionStatus;
  clientRelease: string | null;
  servedRelease: string | null;
  updateAvailable: boolean;
  manualRecoveryRequired: boolean;
};

export type AppVersionEvent =
  | "check_started"
  | "check_failed"
  | "check_unreachable"
  | "version_current"
  | "version_mismatch"
  | "preload_error"
  | "preload_error_during_reload"
  | "reload_deferred_offline"
  | "reload_attempted"
  | "reload_blocked"
  | "manual_reload"
  | "recovery_confirmed";

const RECOVERY_OWNED_STATUSES = new Set<AppVersionStatus>([
  "reload_deferred_offline",
  "reload_attempted",
  "manual_recovery_required",
]);

type ReloadMarker = {
  schemaVersion: 1;
  sourceRelease: string;
  targetRelease: string | null;
  trigger: AppVersionRecoveryTrigger;
  attemptedAt: number;
};

type RecoveryContext = {
  routeFamily?: string;
  failedAssetPath?: string;
};

type AppVersionCoordinatorOptions = {
  clientRelease: string;
  fetchLatestRelease?: () => Promise<string>;
  storage?: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null;
  now?: () => number;
  reload?: () => void;
  isOnline?: () => boolean;
  getVisibilityState?: () => DocumentVisibilityState;
  pollIntervalMs?: number;
  minCheckIntervalMs?: number;
  reloadMarkerTtlMs?: number;
  reloadWatchdogMs?: number;
  report?: (event: AppVersionEvent, data: Record<string, unknown>, error?: unknown) => void;
};

function parseReloadMarker(value: string | null): ReloadMarker | null {
  if (!value) {
    return null;
  }

  try {
    const marker = JSON.parse(value) as Partial<ReloadMarker>;
    if (
      marker.schemaVersion !== RELOAD_MARKER_SCHEMA_VERSION ||
      typeof marker.sourceRelease !== "string" ||
      (marker.targetRelease !== null && typeof marker.targetRelease !== "string") ||
      !["bootstrap_mismatch", "navigation_mismatch", "preload_error"].includes(
        marker.trigger ?? ""
      ) ||
      typeof marker.attemptedAt !== "number"
    ) {
      return null;
    }
    return marker as ReloadMarker;
  } catch {
    return null;
  }
}

function getSessionStorage(): Storage | null {
  try {
    return globalThis.sessionStorage;
  } catch {
    return null;
  }
}

export class AppVersionCoordinator {
  readonly clientRelease: string | null;

  private readonly fetchLatestRelease: () => Promise<string>;
  private readonly storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null;
  private readonly now: () => number;
  private readonly reload: () => void;
  private readonly isOnline: () => boolean;
  private readonly getVisibilityState: () => DocumentVisibilityState;
  private readonly pollIntervalMs: number;
  private readonly minCheckIntervalMs: number;
  private readonly reloadMarkerTtlMs: number;
  private readonly reloadWatchdogMs: number;
  private readonly report: (
    event: AppVersionEvent,
    data: Record<string, unknown>,
    error?: unknown
  ) => void;
  private readonly listeners = new Set<() => void>();

  private snapshot: AppVersionSnapshot;
  private started = false;
  private lastCheckAt = Number.NEGATIVE_INFINITY;
  private checkPromise: Promise<void> | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private memoryMarker: ReloadMarker | null = null;
  private preloadRecoveryHandled = false;
  private preloadErrorDuringReload: { data: Record<string, unknown>; error: unknown } | null = null;
  private consecutiveUnreachableChecks = 0;
  private deferredReload: {
    trigger: AppVersionRecoveryTrigger;
    targetRelease: string | null;
  } | null = null;

  constructor(options: AppVersionCoordinatorOptions) {
    this.clientRelease = normalizeDeploymentVersion(options.clientRelease);
    this.fetchLatestRelease =
      options.fetchLatestRelease ??
      (async () => {
        const config = await fetchLatestRuntimeConfig();
        return config.release;
      });
    this.storage = options.storage === undefined ? getSessionStorage() : options.storage;
    this.now = options.now ?? Date.now;
    this.reload = options.reload ?? (() => globalThis.location.reload());
    this.isOnline = options.isOnline ?? (() => globalThis.navigator.onLine !== false);
    this.getVisibilityState =
      options.getVisibilityState ?? (() => globalThis.document.visibilityState);
    this.pollIntervalMs = options.pollIntervalMs ?? APP_VERSION_POLL_INTERVAL_MS;
    this.minCheckIntervalMs = options.minCheckIntervalMs ?? APP_VERSION_MIN_CHECK_INTERVAL_MS;
    this.reloadMarkerTtlMs = options.reloadMarkerTtlMs ?? APP_VERSION_RELOAD_MARKER_TTL_MS;
    this.reloadWatchdogMs = options.reloadWatchdogMs ?? APP_VERSION_RELOAD_WATCHDOG_MS;
    this.report = options.report ?? (() => undefined);
    this.snapshot = {
      status: "current",
      clientRelease: this.clientRelease,
      servedRelease: null,
      updateAvailable: false,
      manualRecoveryRequired: false,
    };
  }

  getSnapshot = (): AppVersionSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  start(bootstrapRelease: string): void {
    if (this.started) {
      return;
    }
    this.started = true;

    const marker = this.readMarker();
    if (marker && marker.sourceRelease !== this.clientRelease) {
      this.clearMarker();
      this.emit("recovery_confirmed", {
        trigger: marker.trigger,
        sourceRelease: marker.sourceRelease,
        targetRelease: marker.targetRelease,
        clientRelease: this.clientRelease,
      });
    }

    const comparison = compareDeploymentVersions(this.clientRelease ?? "", bootstrapRelease);
    this.applyComparison(comparison.servedRelease, comparison.status, "bootstrap");

    if (comparison.status === "different") {
      this.requestAutomaticReload("bootstrap_mismatch", comparison.servedRelease);
    }

    if (this.clientRelease === "local") {
      return;
    }

    globalThis.document.addEventListener("visibilitychange", this.handleVisibilityChange);
    globalThis.window.addEventListener("online", this.handleOnline);
    globalThis.window.addEventListener("vite:preloadError", this.handlePreloadError);
    this.intervalId = setInterval(() => {
      if (this.getVisibilityState() === "visible" && this.isOnline()) {
        void this.checkForUpdate();
      }
    }, this.pollIntervalMs);
  }

  stop(): void {
    if (!this.started) {
      return;
    }
    this.started = false;
    globalThis.document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    globalThis.window.removeEventListener("online", this.handleOnline);
    globalThis.window.removeEventListener("vite:preloadError", this.handlePreloadError);
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  checkForUpdate(options: { force?: boolean } = {}): Promise<void> {
    if (this.clientRelease === "local") {
      return Promise.resolve();
    }
    if (RECOVERY_OWNED_STATUSES.has(this.snapshot.status)) {
      return Promise.resolve();
    }
    if (this.checkPromise) {
      return this.checkPromise;
    }

    const checkedAt = this.now();
    if (!options.force && checkedAt - this.lastCheckAt < this.minCheckIntervalMs) {
      return Promise.resolve();
    }
    this.lastCheckAt = checkedAt;
    this.transition({
      status: "checking",
      manualRecoveryRequired: false,
    });
    this.emit("check_started", { clientRelease: this.clientRelease });

    this.checkPromise = this.fetchLatestRelease()
      .then((release) => {
        if (RECOVERY_OWNED_STATUSES.has(this.snapshot.status)) {
          return;
        }
        this.consecutiveUnreachableChecks = 0;
        const comparison = compareDeploymentVersions(this.clientRelease ?? "", release);
        this.applyComparison(comparison.servedRelease, comparison.status, "poll");
      })
      .catch((error: unknown) => {
        if (RECOVERY_OWNED_STATUSES.has(this.snapshot.status)) {
          return;
        }
        this.transition({
          status: this.snapshot.updateAvailable ? "update_available" : "current",
        });
        const data: Record<string, unknown> = {
          clientRelease: this.clientRelease,
          errorName: error instanceof Error ? error.name : "UnknownError",
        };
        // A transport failure clears up on its own unless it persists while online; an HTTP or
        // body failure is a deployment problem and is reported at once.
        if (isNetworkFetchFailure(error)) {
          const online = this.isOnline();
          if (online) {
            this.consecutiveUnreachableChecks += 1;
          }
          data.consecutiveFailures = this.consecutiveUnreachableChecks;
          if (
            !online ||
            this.consecutiveUnreachableChecks !== APP_VERSION_UNREACHABLE_CHECKS_BEFORE_FAILURE
          ) {
            this.emit("check_unreachable", data);
            return;
          }
        }
        this.emit("check_failed", data, error);
      })
      .finally(() => {
        this.checkPromise = null;
      });

    return this.checkPromise;
  }

  requestAutomaticReload(
    trigger: AppVersionRecoveryTrigger,
    targetRelease: string | null,
    context: RecoveryContext = {}
  ): boolean {
    if (!this.prepareAutomaticReload(trigger, targetRelease, context)) {
      return false;
    }
    this.reload();
    return true;
  }

  prepareNavigationReload(intendedPath?: string): boolean {
    if (!this.snapshot.updateAvailable) {
      return false;
    }
    return this.prepareAutomaticReload("navigation_mismatch", this.snapshot.servedRelease, {
      routeFamily: intendedPath ? getRouteFamily(intendedPath) : undefined,
    });
  }

  requestManualReload(): void {
    this.emit("manual_reload", {
      clientRelease: this.clientRelease,
      servedRelease: this.snapshot.servedRelease,
    });
    this.reload();
  }

  private prepareAutomaticReload(
    trigger: AppVersionRecoveryTrigger,
    targetRelease: string | null,
    context: RecoveryContext = {}
  ): boolean {
    if (!this.clientRelease || this.clientRelease === "local") {
      return false;
    }
    if (
      this.snapshot.status === "reload_attempted" ||
      this.snapshot.status === "manual_recovery_required"
    ) {
      return false;
    }

    if (!this.isOnline()) {
      this.deferredReload = { trigger, targetRelease };
      this.transition({
        status: "reload_deferred_offline",
        servedRelease: targetRelease,
        updateAvailable: targetRelease !== null,
      });
      this.emit("reload_deferred_offline", {
        trigger,
        clientRelease: this.clientRelease,
        targetRelease,
        ...context,
      });
      return false;
    }

    this.deferredReload = null;
    const existingMarker = this.readMarker();
    if (
      existingMarker &&
      existingMarker.sourceRelease === this.clientRelease &&
      this.now() - existingMarker.attemptedAt <= this.reloadMarkerTtlMs
    ) {
      this.transition({
        status: "manual_recovery_required",
        servedRelease: targetRelease ?? existingMarker.targetRelease,
        updateAvailable: true,
        manualRecoveryRequired: true,
      });
      this.emit("reload_blocked", {
        trigger,
        previousTrigger: existingMarker.trigger,
        clientRelease: this.clientRelease,
        targetRelease,
        ...context,
      });
      return false;
    }

    const marker: ReloadMarker = {
      schemaVersion: RELOAD_MARKER_SCHEMA_VERSION,
      sourceRelease: this.clientRelease,
      targetRelease,
      trigger,
      attemptedAt: this.now(),
    };
    this.writeMarker(marker);
    this.transition({
      status: "reload_attempted",
      servedRelease: targetRelease,
      updateAvailable: true,
    });
    this.emit("reload_attempted", {
      trigger,
      clientRelease: this.clientRelease,
      targetRelease,
      ...context,
    });
    // shortcut: a reload whose response takes longer than the watchdog to commit reads as
    // stalled and captures any held preload error; upgrade if stalledReload events show it.
    setTimeout(() => {
      if (this.snapshot.status === "reload_attempted") {
        this.transition({
          status: "manual_recovery_required",
          manualRecoveryRequired: true,
        });
        const held = this.preloadErrorDuringReload;
        this.preloadErrorDuringReload = null;
        if (held) {
          this.emit("preload_error", { ...held.data, stalledReload: true }, held.error);
        }
      }
    }, this.reloadWatchdogMs);
    return true;
  }

  private readonly handleVisibilityChange = () => {
    if (this.getVisibilityState() === "visible" && this.isOnline()) {
      void this.checkForUpdate();
    }
  };

  private readonly handleOnline = () => {
    const deferredReload = this.deferredReload;
    if (deferredReload) {
      this.requestAutomaticReload(deferredReload.trigger, deferredReload.targetRelease);
      return;
    }
    if (this.getVisibilityState() === "visible") {
      void this.checkForUpdate();
    }
  };

  private readonly handlePreloadError = (event: Event) => {
    const payload = "payload" in event ? event.payload : undefined;
    markPreloadErrorHandled(payload);

    if (this.preloadRecoveryHandled) {
      return;
    }
    this.preloadRecoveryHandled = true;

    const failedAssetPath = getFailedAssetPath(payload);
    const data = {
      clientRelease: this.clientRelease,
      servedRelease: this.snapshot.servedRelease,
      errorName: payload instanceof Error ? payload.name : "UnknownError",
      failedAssetPath,
    };
    if (
      this.snapshot.status === "reload_attempted" ||
      this.requestAutomaticReload(
        "preload_error",
        this.snapshot.updateAvailable ? this.snapshot.servedRelease : null,
        { failedAssetPath }
      )
    ) {
      // The reload already under way, or the one this error just started, replaces this
      // document; the error matters only if the watchdog finds the document still here.
      this.preloadErrorDuringReload = { data, error: payload };
      this.emit("preload_error_during_reload", data);
      return;
    }
    this.emit("preload_error", data, payload);
  };

  private applyComparison(
    servedRelease: string | null,
    status: "same" | "different" | "unknown",
    source: "bootstrap" | "poll"
  ): void {
    if (status === "different") {
      this.transition({
        status: "update_available",
        servedRelease,
        updateAvailable: true,
        manualRecoveryRequired: false,
      });
      this.emit("version_mismatch", {
        source,
        clientRelease: this.clientRelease,
        servedRelease,
      });
      return;
    }

    if (status === "unknown") {
      this.transition({
        status: this.snapshot.updateAvailable ? "update_available" : "current",
      });
      return;
    }

    this.transition({
      status: "current",
      servedRelease,
      updateAvailable: false,
      manualRecoveryRequired: false,
    });
    this.emit("version_current", {
      source,
      clientRelease: this.clientRelease,
    });
  }

  private transition(patch: Partial<AppVersionSnapshot>): void {
    const next = { ...this.snapshot, ...patch };
    if (
      next.status === this.snapshot.status &&
      next.clientRelease === this.snapshot.clientRelease &&
      next.servedRelease === this.snapshot.servedRelease &&
      next.updateAvailable === this.snapshot.updateAvailable &&
      next.manualRecoveryRequired === this.snapshot.manualRecoveryRequired
    ) {
      return;
    }
    this.snapshot = next;
    for (const listener of this.listeners) {
      listener();
    }
  }

  private emit(event: AppVersionEvent, data: Record<string, unknown>, error?: unknown): void {
    this.report(
      event,
      {
        ...data,
        online: this.isOnline(),
        visibilityState: this.getVisibilityState(),
      },
      error
    );
  }

  private readMarker(): ReloadMarker | null {
    let marker = this.memoryMarker;
    try {
      marker =
        parseReloadMarker(this.storage?.getItem(APP_VERSION_RELOAD_MARKER_KEY) ?? null) ?? marker;
    } catch {
      // sessionStorage can be unavailable under strict browser privacy settings.
    }

    if (marker && this.now() - marker.attemptedAt > this.reloadMarkerTtlMs) {
      this.clearMarker();
      return null;
    }
    return marker;
  }

  private writeMarker(marker: ReloadMarker): void {
    this.memoryMarker = marker;
    try {
      this.storage?.setItem(APP_VERSION_RELOAD_MARKER_KEY, JSON.stringify(marker));
    } catch {
      // The in-memory marker still provides a one-document loop guard.
    }
  }

  private clearMarker(): void {
    this.memoryMarker = null;
    try {
      this.storage?.removeItem(APP_VERSION_RELOAD_MARKER_KEY);
    } catch {
      // There is no recovery action needed when storage cleanup is unavailable.
    }
  }
}

export function getRouteFamily(pathname: string): string {
  if (pathname === "/") return "home";
  if (pathname.startsWith("/project/")) return "project";
  if (pathname === "/df-admin" || pathname.startsWith("/df-admin/")) return "admin";
  if (pathname === "/settings" || pathname.startsWith("/settings/")) return "settings";
  if (pathname === "/auth" || pathname.startsWith("/auth/")) return "auth";
  if (pathname === "/error" || pathname.startsWith("/error/")) return "error";
  return "other";
}

export function getFailedAssetPath(error: unknown): string | undefined {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "object" &&
          error !== null &&
          "message" in error &&
          typeof error.message === "string"
        ? error.message
        : typeof error === "string"
          ? error
          : "";
  const match = message.match(/(?:https?:\/\/[^\s"'`]+)?(\/assets\/[^\s"'`)]+)/i);
  return match?.[1];
}

let activeCoordinator: AppVersionCoordinator | null = null;

export function registerAppVersionCoordinator(coordinator: AppVersionCoordinator): void {
  activeCoordinator?.stop();
  activeCoordinator = coordinator;
}

export function getAppVersionCoordinator(): AppVersionCoordinator | null {
  return activeCoordinator;
}
